import { Box, Code, Flex, Heading } from '@radix-ui/themes';
import { MachineContextHelper } from 'classes/helpers/machine-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonDateInput } from 'components/common/form/date';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTextInput } from 'components/common/form/text';
import { PlotModelMetrics } from 'components/sections/admin-portal/machine-models/metrics/plot-model-metrics';
import { PrintMetric } from 'components/sections/admin-portal/machine-models/metrics/print-metric';
import { SummarizeModelMetrics } from 'components/sections/admin-portal/machine-models/metrics/summarize-model-metrics';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineModelsContext } from 'contexts/admin/machine-models.context';
import { endOfHour, endOfToday, startOfToday, subWeeks } from 'date-fns';
import { t } from 'i18next';
import { DEFAULT_ACCEPT_BTN, IFullDialog } from 'interfaces/i-dialogs';
import { DEFAULT_MIN_GROUPS, ModelHelper } from 'lib_ts/classes/model.helper';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IEvalModelResult } from 'lib_ts/interfaces/modelling/i-eval-models';
import { IGatherShotDataQuery } from 'lib_ts/interfaces/modelling/i-gather-shot-data';
import { IMachineModel } from 'lib_ts/interfaces/modelling/i-machine-model';
import { useContext, useMemo, useState } from 'react';

// aka: compare model performance
const COMPONENT_NAME = 'ModelPerformanceDialog';

const BALL_TYPE_OPTIONS = Object.values(BallType).map((m) => ({
  label: m,
  value: m,
}));

const DEFAULT_START_DATE = subWeeks(startOfToday(), 2);
const DEFAULT_END_DATE = endOfHour(new Date());

const DEFAULT_DATA_FILTER = ModelHelper.getDefaultDataFilter(
  DEFAULT_START_DATE,
  DEFAULT_END_DATE
);

interface IProps {
  models: IMachineModel[];
  onClose: () => void;
}

export const ModelPerformanceDialog = ({ models, onClose }: IProps) => {
  const machineModelsCx = useContext(MachineModelsContext);
  const firstModel = models[0];

  const [modelEvaluations, setModelEvaluations] =
    useState<IEvalModelResult[]>();

  const [dataFilter, setDataFilter] = useState(() => {
    const output: IGatherShotDataQuery = {
      machineID: firstModel?.machineID,
      ball_type: !firstModel ? undefined : ModelHelper.getBallType(firstModel),
      start_date: DEFAULT_DATA_FILTER.start_date,
      end_date: DEFAULT_DATA_FILTER.end_date,
      min_groups: DEFAULT_DATA_FILTER.min_groups,
      max_groups: DEFAULT_DATA_FILTER.max_groups,
      limit: DEFAULT_DATA_FILTER.limit,
      training: true,
      check_confidence: false,
      check_spin: false,
      check_breaks: false,
    };

    return output;
  });

  const updateDataFilter = (filter: Partial<IGatherShotDataQuery>) => {
    setDataFilter((prev) => ({ ...prev, ...filter }));
  };

  const onAccept = async () => {
    if (!dataFilter.machineID) {
      NotifyHelper.warning({
        message_md: 'Please select a machine and try again.',
      });
      return;
    }

    if (!dataFilter.ball_type) {
      NotifyHelper.warning({
        message_md: 'Please select a ball type and try again.',
      });
      return;
    }

    if (
      dataFilter.min_groups === undefined ||
      dataFilter.min_groups < DEFAULT_MIN_GROUPS
    ) {
      NotifyHelper.warning({
        message_md: `Please provide a min groups value that is at least ${DEFAULT_MIN_GROUPS} and try again.`,
      });
      return;
    }

    try {
      const results = await machineModelsCx.evalModelMetrics(
        models.map((m) => m._id),
        dataFilter
      );
      setModelEvaluations(results);
    } catch (error) {
      console.error(error);
      NotifyHelper.error({
        message_md: `An error occurred while evaluating models: ${error}`,
      });
    }
  };

  const hasResults = modelEvaluations && modelEvaluations.length > 0;
  const content = hasResults ? (
    <ModelPerformanceResults modelEvaluations={modelEvaluations} />
  ) : (
    <ModelPerformanceForm
      models={models}
      dataFilter={dataFilter}
      updateDataFilter={updateDataFilter}
    />
  );

  const mergeProps: IFullDialog = {
    identifier: COMPONENT_NAME,
    title: models.length === 1 ? 'Model Performance' : 'Compare Models',
    width: RADIX.DIALOG.WIDTH.XL,
    loading: machineModelsCx.loading,
    content,
    buttons: [
      {
        ...DEFAULT_ACCEPT_BTN,
        invisible: hasResults,
        onClick: onAccept,
      },
    ],
    onClose,
  };

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog {...mergeProps} />
    </ErrorBoundary>
  );
};

export const ModelPerformanceForm = ({
  models,
  dataFilter,
  updateDataFilter,
}: IFormProps) => {
  const machines = useMachinesPortalStore((s) => s.machines);

  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);

  const machineOptions = useMemo(
    () =>
      machines.map((o) => ({
        label: MachineContextHelper.getMachineMenuLabel(o),
        value: o.machineID,
      })),
    [machines]
  );

  return (
    <CommonFormGrid columns={3}>
      {models.length > 1 && (
        <Box>
          <Heading size={RADIX.HEADING.SIZE.SM}>Models to be compared:</Heading>
          <ul>
            {models.map((m) => (
              <li key={`model-${m._id}`}>{m.name}</li>
            ))}
          </ul>
        </Box>
      )}

      {models.length === 1 && (
        <Box>
          <Heading size={RADIX.HEADING.SIZE.SM}>Model:</Heading>
          <ul>
            <li> Name: {models[0].name} </li>
            <li>
              ID: <Code>{models[0]._id}</Code>
            </li>
          </ul>
        </Box>
      )}

      <Box gridColumn="span 3">
        <CommonCallout
          text={`Use the form below to determine which pitches should be used for evaluating the ${
            models.length === 1 ? 'model' : 'models'
          }.`}
        />
      </Box>

      <Box gridColumn="1">
        <CommonSearchInput
          id="machineID"
          label="common.machine"
          values={dataFilter.machineID ? [dataFilter.machineID] : []}
          options={machineOptions}
          onChange={(machineIDs) => {
            updateDataFilter({ machineID: machineIDs[0] });
          }}
        />
      </Box>
      <CommonSelectInput
        id="model-performance-ball"
        name="ball_type"
        label="common.ball-type"
        options={BALL_TYPE_OPTIONS}
        optional
        value={dataFilter.ball_type}
        onChange={(v) => updateDataFilter({ ball_type: v as BallType })}
      />
      <CommonTextInput
        id="create-model-collectionID"
        label="Collection ID"
        name="collectionID"
        value={dataFilter.collectionID}
        onChange={(v) => updateDataFilter({ collectionID: v })}
        hint_md='Like "AAAAAAAA"'
        optional
      />
      <CommonDateInput
        id="model-performance-start"
        label="common.start-date"
        defaultValue={startDate}
        maxDate={endDate}
        onChange={(date) => {
          if (!date) {
            NotifyHelper.warning({
              message_md: t('common.check-inputs-msg'),
            });
            return;
          }
          setStartDate(date);
          updateDataFilter({ start_date: date.toISOString() });
        }}
        showTime
      />
      <CommonDateInput
        id="model-performance-end"
        label="common.end-date"
        defaultValue={endDate}
        minDate={startDate}
        maxDate={endOfToday()}
        onChange={(date) => {
          if (!date) {
            NotifyHelper.warning({
              message_md: t('common.check-inputs-msg'),
            });
            return;
          }
          setEndDate(date);
          updateDataFilter({ end_date: date.toISOString() });
        }}
        showTime
      />
      <CommonTextInput
        id="model-performance-session"
        label="Session"
        name="session"
        value={dataFilter.session}
        onChange={(v) => updateDataFilter({ session: v })}
        hint_md='Like "AAAAAAAA-...-BBBB.X"'
        optional
      />

      <Box gridColumn="1">
        <CommonTextInput
          id="model-performance-min-groups"
          label="Min Groups"
          type="number"
          name="min_groups"
          value={dataFilter.min_groups?.toString()}
          placeholder="Type an integer value"
          onOptionalNumericChange={(v) => {
            updateDataFilter({
              min_groups: v === undefined ? undefined : Math.round(v),
            });
          }}
          hint_md="Minimum number of unique target machine states."
        />
      </Box>
      <CommonTextInput
        id="model-performance-max-groups"
        label="Max Groups"
        type="number"
        name="max_groups"
        value={dataFilter.max_groups?.toString()}
        placeholder="Type an integer value"
        onOptionalNumericChange={(e) => {
          updateDataFilter({
            max_groups: e === undefined ? undefined : Math.round(e),
          });
        }}
        hint_md="Maximum number of unique target machine states."
        optional
      />
      <CommonTextInput
        id="model-performance-record-limit"
        label="Record Limit"
        type="number"
        name="limit"
        value={dataFilter.limit?.toString()}
        placeholder="Type an integer value"
        onOptionalNumericChange={(e) => {
          updateDataFilter({
            limit: e === undefined ? undefined : Math.round(e),
          });
        }}
        hint_md="Query limit on maximum number of records to use."
        optional
      />
    </CommonFormGrid>
  );
};

interface IResultsProps {
  modelEvaluations: IEvalModelResult[];
}

const ModelPerformanceResults = ({ modelEvaluations }: IResultsProps) => (
  <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
    <Heading size={RADIX.HEADING.SIZE.SM}>Results</Heading>
    <PlotModelMetrics metrics={modelEvaluations} />
    <SummarizeModelMetrics metrics={modelEvaluations} />
    {modelEvaluations.map((m, i) => (
      <PrintMetric key={`print-metric-${i}`} metric={m} />
    ))}
  </Flex>
);

interface IFormProps {
  models: IMachineModel[];
  dataFilter: IGatherShotDataQuery;
  updateDataFilter: (filter: Partial<IGatherShotDataQuery>) => void;
}
